/**
 * 項目名や数値にカーソルを合わせたときの説明(⑦指示: ブラウザ標準の title は1行で読みづらい)。
 *  1. 先頭に「今の値が良いか悪いか」(判定)と、その理由
 *  2. 指標の意味(計算式)と、判定の向き(高いほど良い／低いほど良い)
 *  3. 色の意味(色見本つき)
 * 吹き出しは body 直下に position:fixed で描き、画面右端・下端では位置を反転する。
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/** 色の意味1行(cls は base.css の cf-* クラス。sw-none=色なし) */
export type HelpColor = { cls: string; range: string; meaning: string };

export type Help = {
  title: string;
  /** 指標の意味・計算式 */
  what: string;
  /** 判定の向き(例: 高いほど良い) */
  better: string;
  colors?: HelpColor[];
  /** 出典(現行シート名など) */
  source?: string;
};

export type VerdictLevel = 'good' | 'bad' | 'normal';
export type Verdict = { level: VerdictLevel; reason: string };
export const VERDICT_LABELS: Record<VerdictLevel, string> = { good: '良い', bad: '悪い', normal: '普通' };

const BOX_W = 300;
const BOX_H_GUESS = 260;

export default function HelpTip({
  help,
  verdict,
  value,
  children,
  className,
  style,
}: {
  help: Help;
  /** 数値セルに付けるときの判定(先頭に大きく出す) */
  verdict?: Verdict | null;
  /** 判定と一緒に出す今の値(表示文字列) */
  value?: string;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number } | null>(null);

  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - BOX_W - 8));
    if (r.bottom + BOX_H_GUESS > window.innerHeight && r.top > BOX_H_GUESS) setPos({ left, bottom: window.innerHeight - r.top + 6 });
    else setPos({ left, top: r.bottom + 6 });
  };
  const hide = () => setPos(null);

  // スクロールしたら閉じる(fixed の吹き出しが置き去りにならないように)
  useEffect(() => {
    if (!pos) return;
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [pos]);

  return (
    <span ref={ref} className={`tip${className ? ` ${className}` : ''}`} style={style} onMouseEnter={show} onMouseLeave={hide} onTouchStart={() => (pos ? hide() : show())}>
      {children}
      {pos &&
        createPortal(
          <div className="tip-box" style={pos} role="tooltip">
            {verdict && (
              <div className={`tip-verdict ${verdict.level}`}>
                <span>{VERDICT_LABELS[verdict.level]}</span>
                {value !== undefined && <span className="tip-value">今の値: {value}</span>}
                <span className="tip-reason">{verdict.reason}</span>
              </div>
            )}
            <div className="tip-title">{help.title}</div>
            <div className="tip-what">{help.what}</div>
            <div className="tip-better">判定の向き: {help.better}</div>
            {help.colors && help.colors.length > 0 && (
              <div className="tip-colors">
                {help.colors.map((c, i) => (
                  <span key={i} style={{ display: 'contents' }}>
                    <span className={`tip-sw ${c.cls}`} />
                    <span className="tip-range">{c.range}</span>
                    <span>{c.meaning}</span>
                  </span>
                ))}
              </div>
            )}
            {help.source && <div className="tip-src">出典: {help.source}</div>}
          </div>,
          document.body,
        )}
    </span>
  );
}
